/**
 * CondoDocumentsSellerView — the seller builds, attests and delivers the
 * § 703.33 package, then follows the buyer's receipt. The buyer's review
 * period runs from delivery; the buyer's acknowledgement is only evidence.
 */

import React, { useEffect, useRef, useState } from 'react';
import styles from './CondoDocumentsPanel.module.css';
import {
  CONDO_DOCUMENTS_COPY as COPY,
  CONDO_SMALL_CONDO_QUESTION,
  CONDO_WARNING_COPY,
  SELLER_ATTESTATIONS_BY_KIND,
} from './condoDocumentsCopy';
import {
  ACCEPTED_UPLOAD_TYPES,
  MAX_UPLOAD_BYTES,
  checklistForPackage,
  cureDate as cureDateFor,
  deliverByDate,
  filesForItem,
  hasReportedMissing,
  initialPackage,
  isDelivered,
  isLateMissingRequest,
  isNaEligible,
  lateRequestFor,
  itemEntry,
  itemLabel,
  isSmallCondoPackage,
  labelForCode,
  notApplicablePreset,
  openDraft,
  openMissingRequest,
  packageProgress,
  rawDeliverByDate,
  receiptUnconfirmedText,
} from './condoDocumentsModel';
import {
  addCalendarDays,
  compareYmd,
  formatLongLocalDate,
  formatLongTimestamp,
  todayLocalYmd,
} from './condoDocumentsFormat';
import {
  PACKAGE_KIND_AMENDMENT,
  PACKAGE_KIND_INITIAL,
  PACKAGE_KIND_SUPPLEMENT,
  PACKAGE_STATUS_RECEIVED,
} from '../../agents/condoDocumentsAgent';
import CondoDocumentList from './CondoDocumentList';

const SERVER_TIMING_CODES = ['condo_docs_due_soon', 'condo_docs_late'];
const RECEIPT_UNCONFIRMED_CODE = 'condo_receipt_unconfirmed';
const DUE_SOON_LEAD_DAYS = 3;

/**
 * The 15-day rule as seen before delivery, in the legal warning wording
 * (`WI.condo_docs.warning.condo_docs_{late,due_soon}`). Only used when the
 * server hasn't already returned its own timing warning, so the two never
 * double up. "Late" is judged on the unadjusted date; the date shown is moved
 * back off a weekend (legal §5.4).
 */
export function deliveryTimingNotice(summary, today = todayLocalYmd()) {
  const serverCodes = (summary?.warnings || []).map((w) => w.code);
  if (SERVER_TIMING_CODES.some((code) => serverCodes.includes(code))) return null;
  const closing = summary?.closing_date;
  const raw = rawDeliverByDate(summary);
  if (!closing || !raw) return null;
  const closingDate = formatLongLocalDate(closing);
  if (compareYmd(today, raw) > 0) {
    return { kind: 'late', text: CONDO_WARNING_COPY.condo_docs_late({ closingDate }) };
  }
  const shown = deliverByDate(summary);
  if (compareYmd(today, addCalendarDays(shown, -DUE_SOON_LEAD_DAYS)) >= 0) {
    return {
      kind: 'due_soon',
      text: CONDO_WARNING_COPY.condo_docs_due_soon({
        closingDate,
        deliverByDate: formatLongLocalDate(shown),
      }),
    };
  }
  return null;
}

function ChecklistRow({ index, item, pkg, preset, disabled, busy, onUpload, onRemove, onSetItem }) {
  const code = item.code;
  const label = itemLabel(item);
  const files = filesForItem(pkg, code);
  const entry = itemEntry(pkg, code);
  const notApplicable = entry.not_applicable === true;
  // Legal §5.2: some items must have a file; the rest may be marked N/A.
  const naEligible = isNaEligible(item);
  const [naOpen, setNaOpen] = useState(false);
  const [note, setNote] = useState('');
  const [rowError, setRowError] = useState(null);
  const inputId = `condo-upload-${pkg.id}-${code}`;
  const noteId = `condo-na-note-${pkg.id}-${code}`;
  const uploading = busy === `upload:${code}`;

  const amendment = pkg.kind === PACKAGE_KIND_AMENDMENT;
  let status = amendment
    ? { text: COPY.itemNoChangeUploaded, className: styles.chipNa }
    : { text: COPY.itemPending, className: styles.chipPending };
  if (files.length > 0) status = { text: COPY.itemReady, className: styles.chipReady };
  else if (notApplicable) status = { text: COPY.itemNotApplicable, className: styles.chipNa };

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setRowError(COPY.fileTooLarge({ name: file.name }));
      return;
    }
    setRowError(null);
    await onUpload(code, file);
  };

  const handleSaveNa = async (event) => {
    event.preventDefault();
    if (!note.trim()) {
      setRowError(COPY.notApplicableNoteRequired);
      return;
    }
    setRowError(null);
    const result = await onSetItem(code, { notApplicable: true, note: note.trim() });
    if (result?.success) {
      setNaOpen(false);
      setNote('');
    }
  };

  return (
    <li
      className={`${styles.row} ${files.length || notApplicable ? styles.rowDone : ''}`}
      data-testid={`condo-item-${code}`}
    >
      <span className={styles.rowIndex} aria-hidden="true">
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className={styles.rowMain}>
        <div className={styles.rowHead}>
          <span className={styles.rowLabel}>{label}</span>
          <span className={styles.badge}>
            {amendment ? COPY.ifChanged : item.required ? COPY.required : COPY.optional}
          </span>
          <span className={`${styles.chip} ${status.className}`}>{status.text}</span>
        </div>

        {files.length > 0 && (
          <ul className={styles.fileList}>
            {files.map((file) => (
              <li key={file.id} className={styles.fileItem}>
                <span className={styles.fileName}>{file.original_filename}</span>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => onRemove(file.id)}
                  disabled={disabled}
                  aria-label={COPY.removeFile({ name: file.original_filename })}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        {notApplicable && (
          <p className={styles.naSaved}>
            {COPY.notApplicableSaved({ note: entry.note || '' })}{' '}
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => onSetItem(code, { notApplicable: false, note: null })}
              disabled={disabled}
            >
              {COPY.undoNotApplicable}
            </button>
          </p>
        )}

        {naOpen && !notApplicable && (
          <form className={styles.naForm} onSubmit={handleSaveNa}>
            <label htmlFor={noteId} className={styles.fieldLabel}>
              {COPY.notApplicableNoteLabel({ label })}
            </label>
            <textarea
              id={noteId}
              className={styles.textarea}
              rows={2}
              value={note}
              placeholder={COPY.notApplicableNotePlaceholder}
              onChange={(e) => setNote(e.target.value)}
              disabled={disabled}
            />
            <div className={styles.inlineActions}>
              <button type="submit" className={styles.buttonSecondary} disabled={disabled}>
                {COPY.saveNotApplicable}
              </button>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => { setNaOpen(false); setRowError(null); }}
              >
                {COPY.dismiss}
              </button>
            </div>
          </form>
        )}

        {rowError && (
          <p className={styles.fieldError} role="alert">{rowError}</p>
        )}

        {!notApplicable && !naOpen && (
          <div className={styles.rowActions}>
            <input
              id={inputId}
              type="file"
              className={styles.visuallyHidden}
              accept={ACCEPTED_UPLOAD_TYPES}
              aria-label={COPY.uploadLabel({ label })}
              onChange={handleFile}
              disabled={disabled}
            />
            <label
              htmlFor={inputId}
              className={`${styles.buttonSecondary} ${styles.uploadLabel} ${disabled ? styles.isDisabled : ''}`}
              aria-hidden="true"
            >
              {uploading ? COPY.uploading : files.length ? COPY.uploadAnother : COPY.uploadButton}
            </label>
            {files.length === 0 && naEligible && !amendment && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => { setNote((current) => current || preset || ''); setNaOpen(true); }}
                disabled={disabled}
              >
                {COPY.markNotApplicable}
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * The small-condominium question (legal §5.2, `WI.condo_docs.small_condo.*`),
 * on the draft initial package only. The answer changes the checklist, so the
 * server re-sends `items` and the panel reloads.
 */
function SmallCondoQuestion({ pkg, question, busy, onAnswer }) {
  const copy = {
    text: question?.text || CONDO_SMALL_CONDO_QUESTION.question,
    yes: question?.option_yes || CONDO_SMALL_CONDO_QUESTION.optionYes,
    no: question?.option_no || CONDO_SMALL_CONDO_QUESTION.optionNo,
  };
  const current = pkg.small_condominium === true;
  const saving = busy === 'small-condominium';
  const name = `condo-small-condo-${pkg.id}`;
  const choose = (value) => {
    if (value !== current) onAnswer(value);
  };

  return (
    <fieldset
      className={`${styles.fieldset} ${styles.smallCondoQuestion}`}
      data-testid="condo-small-condo-question"
      aria-busy={saving}
    >
      <legend className={styles.fieldLabel}>{copy.text}</legend>
      <label className={styles.radioRow}>
        <input
          type="radio"
          name={name}
          checked={current}
          onChange={() => choose(true)}
          disabled={Boolean(busy)}
        />
        <span>{copy.yes}</span>
      </label>
      <label className={styles.radioRow}>
        <input
          type="radio"
          name={name}
          checked={!current}
          onChange={() => choose(false)}
          disabled={Boolean(busy)}
        />
        <span>{copy.no}</span>
      </label>
      {saving && <p className={styles.hint} role="status">{COPY.savingSmallCondo}</p>}
    </fieldset>
  );
}

function ChecklistEditor({ pkg, summary, packages, busy, actions }) {
  const checklist = checklistForPackage(pkg, summary, packages);
  const progress = packageProgress(pkg, checklist);
  const [signatureName, setSignatureName] = useState('');
  const [certify, setCertify] = useState(false);
  const disabled = Boolean(busy);
  // Each package kind signs its own text: a supplement or an amendment isn't
  // "the condominium disclosure materials ... with a cover sheet and index".
  const kind = SELLER_ATTESTATIONS_BY_KIND[pkg.kind] ? pkg.kind : 'initial';
  const attestation = summary?.attestations?.seller_delivery_by_kind?.[kind]
    || (kind === 'initial' ? summary?.attestations?.seller_delivery : null)
    || {};
  const attestationText = attestation.text || SELLER_ATTESTATIONS_BY_KIND[kind].text;
  const attestationVersion = attestation.version || SELLER_ATTESTATIONS_BY_KIND[kind].version;
  const timing = pkg.kind === PACKAGE_KIND_INITIAL ? deliveryTimingNotice(summary) : null;
  const canDeliver = progress.ready && certify && signatureName.trim().length > 0 && !disabled;
  const kindLabel = COPY.packageKind[pkg.kind] || COPY.packageKind.initial;
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const small = isSmallCondoPackage(pkg, packages);

  const handleDeliver = async (event) => {
    event.preventDefault();
    if (!canDeliver) return;
    const result = await actions.deliver(pkg.id, {
      signatureName: signatureName.trim(),
      certify,
      attestationVersion,
    });
    if (result?.success) {
      setSignatureName('');
      setCertify(false);
    }
  };

  return (
    <div className={styles.editor} data-testid="condo-seller-checklist">
      <h4 className={styles.subheading}>
        {COPY.packageHeading({ kind: kindLabel, sequence: pkg.sequence ?? 1 })}
      </h4>
      {pkg.kind === PACKAGE_KIND_INITIAL && (
        <>
          <p className={styles.intro} data-testid="condo-seller-intro">{COPY.sellerIntro}</p>
          <p className={styles.hint}>{COPY.sellerInstructions}</p>
        </>
      )}
      {pkg.kind === PACKAGE_KIND_AMENDMENT && (
        <p className={styles.intro} data-testid="condo-amendment-help">{COPY.sellerAmendmentHelp}</p>
      )}
      {pkg.kind === PACKAGE_KIND_INITIAL && (
        <SmallCondoQuestion
          pkg={pkg}
          question={summary?.small_condominium_question}
          busy={busy}
          onAnswer={(value) => actions.setSmallCondominium(pkg.id, value)}
        />
      )}
      {small && (
        <p className={styles.variantNote} data-testid="condo-small-condo-note">
          {COPY.smallCondoChecklistNote}
        </p>
      )}

      {pkg.kind !== PACKAGE_KIND_AMENDMENT && (
        <div className={styles.progress}>
          <div
            className={styles.progressRail}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.done}
            aria-label={COPY.progress({ done: progress.done, total: progress.total })}
          >
            <span className={styles.progressFill} style={{ width: `${pct}%` }} />
          </div>
          <span className={styles.progressText} data-testid="condo-progress">
            {COPY.progress({ done: progress.done, total: progress.total })}
          </span>
        </div>
      )}

      {timing && (
        <p
          className={`${styles.notice} ${timing.kind === 'late' ? styles.noticeError : styles.noticeWarning}`}
          data-testid={`condo-timing-${timing.kind}`}
        >
          {timing.text}
        </p>
      )}

      <p className={styles.hint}>{COPY.fileTypeHint}</p>
      <ol className={styles.checklist} aria-label={COPY.checklistLabel}>
        {checklist.map((item, index) => (
          <ChecklistRow
            key={item.code}
            index={index}
            item={item}
            pkg={pkg}
            preset={notApplicablePreset(item, small)}
            disabled={disabled}
            busy={busy}
            onUpload={(code, file) => actions.uploadFile(pkg.id, code, file)}
            onRemove={(fileId) => actions.removeFile(pkg.id, fileId)}
            onSetItem={(code, values) => actions.setItem(pkg.id, code, values)}
          />
        ))}
      </ol>

      <form className={styles.attestation} onSubmit={handleDeliver}>
        <h4 className={styles.subheading}>{COPY.attestationHeading}</h4>
        <blockquote className={styles.attestationQuote}>{attestationText}</blockquote>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={certify}
            onChange={(e) => setCertify(e.target.checked)}
            disabled={disabled}
          />
          <span>{COPY.attestationCheckbox}</span>
        </label>
        <label className={styles.fieldLabel} htmlFor={`condo-sign-${pkg.id}`}>
          {COPY.signatureLabel}
        </label>
        <input
          id={`condo-sign-${pkg.id}`}
          type="text"
          className={styles.input}
          value={signatureName}
          autoComplete="name"
          onChange={(e) => setSignatureName(e.target.value)}
          disabled={disabled}
        />
        {!progress.ready && (
          <p className={styles.hint} data-testid="condo-deliver-blocked">
            {pkg.kind === PACKAGE_KIND_AMENDMENT
              ? COPY.amendmentNeedsFile
              : COPY.deliverBlocked({ remaining: progress.remaining })}
          </p>
        )}
        <button type="submit" className={styles.buttonPrimary} disabled={!canDeliver}>
          {busy === 'deliver' ? COPY.delivering : COPY.deliverButton}
        </button>
      </form>
    </div>
  );
}

function DeliveredPackageCard({ pkg, summary, packages, transactionId, openRequest, draft, busy, actions }) {
  const kindLabel = COPY.packageKind[pkg.kind] || COPY.packageKind.initial;
  const items = summary?.items || [];
  const checklist = checklistForPackage(pkg, summary, packages);
  const received = pkg.status === PACKAGE_STATUS_RECEIVED;
  const missing = hasReportedMissing(pkg) ? pkg.buyer_receipt.missing_items : [];
  // A late report (after the § 703.33(3c) period): no cure deadline, but the
  // seller may still answer it with a supplement (L4 §9.5).
  const lateRequest = lateRequestFor(summary, pkg);
  const late = isLateMissingRequest(pkg) || Boolean(lateRequest);
  const canCreateSupplement =
    openRequest && openRequest.id === pkg.id && !draft && pkg.answered_by_package_id == null;
  // A delivered supplement that answers this package's missing-documents request.
  const answeredBy = pkg.answered_by_package_id != null
    ? packages.find((p) => p.id === pkg.answered_by_package_id && isDelivered(p))
    : null;
  const deliveredDate = formatLongTimestamp(pkg.delivered_at);
  // The same sentence as the server warning; say it once.
  const warnedUnconfirmed = (summary?.warnings || []).some((w) => w.code === RECEIPT_UNCONFIRMED_CODE);
  // A late request's local date comes from `clock.late_missing_request`.
  const requestDate = lateRequest?.requested_local_date
    ? formatLongLocalDate(lateRequest.requested_local_date)
    : formatLongTimestamp(lateRequest?.requested_at || pkg.missing_request_at || pkg.buyer_received_at);
  const cure = cureDateFor(summary, pkg);
  const cureDate = cure.localDate ? formatLongLocalDate(cure.localDate) : formatLongTimestamp(cure.at);

  return (
    <article className={styles.packageCard} data-testid={`condo-package-${pkg.id}`}>
      <header className={styles.packageHeader}>
        <h4 className={styles.subheading}>
          {COPY.packageHeading({ kind: kindLabel, sequence: pkg.sequence ?? 1 })}
        </h4>
      </header>
      <p className={styles.statusLine} data-testid={`condo-delivery-status-${pkg.id}`}>
        {COPY.delivered({ deliveredDate })}{' '}
        {received && !missing.length
          ? COPY.sellerBuyerConfirmed({ receivedDate: formatLongTimestamp(pkg.buyer_received_at) })
          : null}
        {!received && !warnedUnconfirmed && deliveredDate
          ? receiptUnconfirmedText(summary, packages, deliveredDate)
          : null}
      </p>
      {missing.length > 0 && answeredBy && (
        <div className={`${styles.notice} ${styles.noticeInfo}`} data-testid="condo-missing-answered">
          <p>
            {COPY.sellerMissingAnswered({
              requestDate,
              sequence: answeredBy.sequence ?? '',
              deliveredDate: formatLongTimestamp(answeredBy.delivered_at),
            })}
          </p>
          <ul className={styles.plainList} aria-label={COPY.missingItemsLabel}>
            {missing.map((code) => <li key={code}>{labelForCode(code, items)}</li>)}
          </ul>
        </div>
      )}
      {missing.length > 0 && !answeredBy && (
        <div
          className={`${styles.notice} ${styles.noticeWarning}`}
          data-testid="condo-missing-request"
          data-late={late ? 'true' : undefined}
        >
          <p>
            {late
              ? COPY.sellerLateRequest({ requestDate })
              : cureDate
                ? COPY.sellerMissingRequest({ requestDate, cureDate })
                : COPY.sellerMissingRequestNoDate({ requestDate })}
          </p>
          <ul className={styles.plainList} aria-label={COPY.missingItemsLabel}>
            {missing.map((code) => <li key={code}>{labelForCode(code, items)}</li>)}
          </ul>
          {canCreateSupplement && (
            <button
              type="button"
              className={styles.buttonPrimary}
              onClick={() => actions.createPackage(PACKAGE_KIND_SUPPLEMENT)}
              disabled={Boolean(busy)}
            >
              {COPY.createSupplement}
            </button>
          )}
        </div>
      )}
      <details className={styles.details}>
        <summary>{COPY.showDocuments}</summary>
        <CondoDocumentList pkg={pkg} checklist={checklist} transactionId={transactionId} />
      </details>
    </article>
  );
}

export default function CondoDocumentsSellerView({ summary, packages, transactionId, busy, actions }) {
  const createAttempted = useRef(false);
  const [createFailed, setCreateFailed] = useState(false);
  const draft = openDraft(packages);
  const initial = initialPackage(packages);
  const delivered = packages.filter(isDelivered);
  const openRequest = openMissingRequest(packages);
  const { createPackage } = actions;

  // No package is pre-created by the server: the seller's first visit starts
  // the initial draft (arch §7.4). A 409 means it already exists; the hook
  // reloads, so either way we land on the checklist.
  useEffect(() => {
    if (packages.length > 0 || createAttempted.current) return;
    createAttempted.current = true;
    createPackage(PACKAGE_KIND_INITIAL).then((result) => {
      if (!result?.success && result?.status !== 409) setCreateFailed(true);
    });
  }, [packages.length, createPackage]);

  if (packages.length === 0) {
    return createFailed ? (
      <button
        type="button"
        className={styles.buttonPrimary}
        onClick={async () => {
          const result = await createPackage(PACKAGE_KIND_INITIAL);
          if (result?.success) setCreateFailed(false);
        }}
        disabled={Boolean(busy)}
      >
        {COPY.startPackage}
      </button>
    ) : (
      <p className={styles.muted} role="status">{COPY.startingPackage}</p>
    );
  }

  const canAmend = initial && isDelivered(initial) && !draft && !openRequest;

  return (
    <div className={styles.view} data-testid="condo-seller-view">
      {delivered.map((pkg) => (
        <DeliveredPackageCard
          key={pkg.id}
          pkg={pkg}
          summary={summary}
          packages={packages}
          transactionId={transactionId}
          openRequest={openRequest}
          draft={draft}
          busy={busy}
          actions={actions}
        />
      ))}
      {draft && (
        <ChecklistEditor
          pkg={draft}
          summary={summary}
          packages={packages}
          busy={busy}
          actions={actions}
        />
      )}
      {canAmend && (
        <div>
          <p className={styles.hint} data-testid="condo-amendment-help">{COPY.sellerAmendmentHelp}</p>
          <button
            type="button"
            className={styles.buttonSecondary}
            onClick={() => actions.createPackage(PACKAGE_KIND_AMENDMENT)}
            disabled={Boolean(busy)}
          >
            {COPY.createAmendment}
          </button>
        </div>
      )}
    </div>
  );
}
